// ============================================================
// TILT — PROGRESSION   (src/progression.js)
// ------------------------------------------------------------
// ROADMAP P1. The persistence layer.
//
// ── THE ONE IDEA ─────────────────────────────────────────────
// App.js must NEVER learn what a backend is. It calls save()
// and read(). Whether that lands in localStorage or in RTDB is
// this file's private business and nobody else's.
//
// This is the same separation as SabotageBus: ONE entry point,
// a swappable driver behind it. It worked there. It works here.
//
//   App.js  ──▶  progression  ──▶  LocalDriver     (today)
//                             └─▶  FirebaseDriver  (P1.2, one line)
//
// ── WHY IT'S STILL LOCALSTORAGE ──────────────────────────────
// Because Firebase rules must ship BEFORE the code that writes
// (P1.1, and Terror learned it the expensive way). `$extra:false`
// rejects the WHOLE transaction for one unlisted field, silently,
// with permission_denied and no useful client error. So the
// driver is written, frozen, and dark. Deploy rules → flip one
// boolean → it's live. Not a rewrite. A switch.
//
// ── WRITE SAFETY (non-negotiable, from the holo-wipe) ────────
//   • NEVER full-object overwrite. Every write is a DELTA.
//   • best[] only ever goes UP (Math.max, server-side too).
//   • cleared[] / gauntlet[] are APPEND-ONLY sets. Never a
//     replace. A concurrent write from another tab must not be
//     able to erase a trial you cleared thirty seconds ago.
//   • Firebase previously wiped a purchased holo card and reset
//     currency without consent. That is the anchor memory. Every
//     write path in IE is guilty until proven delta-based.
//
// ── CURRENCY LANE (P1.3 — STRICT) ────────────────────────────
// Terror writes divineCrests. Snake Eyes writes snakeEyesCoins.
// TILT writes tiltTokens. TILT WRITES NOTHING ELSE, EVER.
// Reads of any currency are universal. Exchanges happen at
// Pierre's Crest Well and NOWHERE ELSE. There is deliberately no
// exchange function in this file and there must never be one.
// ============================================================

import { DEFAULT_LOADOUT, validateLoadout } from "./Powers";

// ── SCHEMA v1 — FROZEN. ───────────────────────────────────────
// Every field here must exist in rules.json before FirebaseDriver
// is enabled. Adding a field = a rules deploy in the SAME push.
// Write it down or lose an afternoon.
//
//   tiltProgress/{uid}/
//     loadout     : ["freeze","split","ghost","anchor"]   (4 ids)
//     best        : { [tableId]: number }                 (monotonic ↑)
//     cleared     : { [tableId]: true }                   (append-only)
//     gauntlet    : { [npcId]:   true }                   (append-only)
//     tiltTokens  : number         ← TILT'S OWN LANE. ONLY THIS.
//     stats       : { launches, drains, sabotagesSent, sabotagesEaten }
//     v           : 1
//
// NOTE the shape change from the old localStorage save: `cleared`
// and `gauntlet` were ARRAYS. They are now MAPS/SETS. That is
// intentional and it is a break — arrays in RTDB are a trap (two
// clients appending to index 3 = one of them loses). Maps merge.
// migrate() below upgrades any old save in place, silently.
export const SCHEMA_V = 1;

export const EMPTY = () => ({
  v: SCHEMA_V,
  loadout: [...DEFAULT_LOADOUT],
  best: {},
  cleared: {},
  gauntlet: {},
  tiltTokens: 0,
  stats: { launches: 0, drains: 0, sabotagesSent: 0, sabotagesEaten: 0 },
});

// ── Coercion. A save file is UNTRUSTED INPUT. ─────────────────
// It came off a disk a user can edit, or off a wire. Treat it the
// same way SabotageBus.validate() treats a packet: assume nothing,
// verify everything, never throw. A corrupt save must degrade to a
// fresh save, not to a white screen.
const asSet = (v) => {
  const out = {};
  if (Array.isArray(v)) { for (const k of v) if (k) out[String(k)] = true; }
  else if (v && typeof v === "object") {
    for (const k of Object.keys(v)) if (v[k]) out[k] = true;
  }
  return out;
};
const asNum = (v, d = 0) => (typeof v === "number" && isFinite(v) ? v : d);

export const migrate = (raw) => {
  const e = EMPTY();
  if (!raw || typeof raw !== "object") return e;
  return {
    v: SCHEMA_V,
    loadout: validateLoadout(raw.loadout) || e.loadout,
    best: (() => {
      const b = {};
      const src = raw.best && typeof raw.best === "object" ? raw.best : {};
      for (const k of Object.keys(src)) {
        const n = asNum(src[k], 0);
        if (n > 0) b[k] = n;
      }
      return b;
    })(),
    cleared:  asSet(raw.cleared),   // ← array→map upgrade lives here
    gauntlet: asSet(raw.gauntlet),
    tiltTokens: Math.max(0, asNum(raw.tiltTokens, 0)),
    stats: {
      launches:        Math.max(0, asNum(raw.stats?.launches)),
      drains:          Math.max(0, asNum(raw.stats?.drains)),
      sabotagesSent:   Math.max(0, asNum(raw.stats?.sabotagesSent)),
      sabotagesEaten:  Math.max(0, asNum(raw.stats?.sabotagesEaten)),
    },
  };
};

// ══════════════════════════════════════════════════════════════
// DELTAS — the only way anything is ever written.
// ------------------------------------------------------------
// A delta is a pure (state) => state. It cannot overwrite what it
// didn't ask to touch, because it literally never sees a whole
// object to replace. THIS is what makes the holo-wipe unrepeatable:
// the destructive operation is not expressible.
// ══════════════════════════════════════════════════════════════

/** Best score. MONOTONIC — it can only ever go up. */
export const dBest = (tableId, score) => (s) => {
  const cur = s.best[tableId] || 0;
  const n = Math.max(0, asNum(score, 0));
  if (n <= cur) return s;                     // no-op. don't dirty the save.
  return { ...s, best: { ...s.best, [tableId]: n } };
};

/** Trial cleared. APPEND-ONLY. Never un-clears. */
export const dCleared = (tableId) => (s) =>
  s.cleared[tableId] ? s : { ...s, cleared: { ...s.cleared, [tableId]: true } };

/** Gauntlet opponent defeated. APPEND-ONLY. */
export const dGauntlet = (npcId) => (s) =>
  s.gauntlet[npcId] ? s : { ...s, gauntlet: { ...s.gauntlet, [npcId]: true } };

/** Loadout. The ONE field that is a legitimate replace — it's a
 *  4-slot choice, not an accumulation. Still validated first: a
 *  save with a made-up power id must not reach the engine. */
export const dLoadout = (loadout) => (s) => ({
  ...s, loadout: validateLoadout(loadout) || s.loadout,
});

/** Currency. DELTA ONLY — never "set to N".
 *  A read-modify-write races. `+= n` under a transaction does not.
 *  Clamped at 0: no negative balances, ever, from any code path. */
export const dTokens = (n) => (s) => ({
  ...s, tiltTokens: Math.max(0, s.tiltTokens + asNum(n, 0)),
});

/** Lifetime stat bump. Also a delta, for the same reason. */
export const dStat = (key, n = 1) => (s) =>
  s.stats[key] === undefined ? s
    : { ...s, stats: { ...s.stats, [key]: Math.max(0, s.stats[key] + asNum(n, 1)) } };

// ── Token payout. The only place tokens are ever minted. ──────
// One source of truth, so the economy can be retuned in one line
// instead of hunted across screens. Deliberately stingy: a
// currency you can farm in ten minutes is not a currency.
export const TOKENS = {
  perScore:      (score)  => Math.floor(Math.max(0, score) / 5000),
  trialCleared:  25,
  gauntletWin:   40,
  duelWin:       10,
};

// ══════════════════════════════════════════════════════════════
// DRIVER 1 — LOCAL (today)
// ══════════════════════════════════════════════════════════════
const LS_KEY = "tilt_save_v1";

export const LocalDriver = {
  id: "local",
  async read() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return migrate(raw ? JSON.parse(raw) : null);
    } catch { return EMPTY(); }
  },
  // The local driver has no concurrency, so it CAN write whole —
  // but it applies the delta itself anyway, so both drivers take
  // the identical call signature. Same path, tested in single-
  // player, exactly like LocalTransport. That is the entire trick:
  // you never discover the async race on launch day, because the
  // async path is the one you've been running all along.
  async commit(prev, delta) {
    const next = delta(prev);
    if (next === prev) return prev;            // delta was a no-op
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
    return next;
  },
};

// ══════════════════════════════════════════════════════════════
// DRIVER 2 — FIREBASE (P1.2 — WRITTEN, FROZEN, DARK)
// ------------------------------------------------------------
// ⚠ DO NOT ENABLE UNTIL rules.json SHIPS. Rules FIRST, code
//   second. `$extra:false` rejects the entire transaction for one
//   unlisted field, SILENTLY. This is not a hypothetical; it is a
//   documented afternoon.
//
// Deps are ARGUMENTS, not imports — so this file never drags
// Firebase into the physics bundle, and stays unit-testable.
//
// Every write is runTransaction on the NARROWEST POSSIBLE PATH.
// Not the profile. Not even the user node. The leaf.
//   • best     → tiltProgress/{uid}/best/{tableId}      (max)
//   • tokens   → tiltProgress/{uid}/tiltTokens          (+= n)
//   • cleared  → tiltProgress/{uid}/cleared/{tableId}   (= true)
// Two tabs writing two different tables cannot collide, because
// they are not writing the same node. The blast radius of any
// single write is one leaf.
// ══════════════════════════════════════════════════════════════
export const FirebaseDriver = ({ db, ref, get, runTransaction, uid }) => {
  const root = `tiltProgress/${uid}`;

  return {
    id: "firebase",

    async read() {
      try {
        const snap = await get(ref(db, root));
        return migrate(snap.exists() ? snap.val() : null);
      } catch (e) {
        console.warn("[progression] read failed, falling back to empty:", e?.message);
        return EMPTY();   // a dead network is not a reason to lose a session
      }
    },

    async commit(prev, delta) {
      const next = delta(prev);
      if (next === prev) return prev;

      // Diff the delta down to leaves. We write ONLY what changed.
      // This is the whole write-safety story in one loop: it is not
      // possible to express "replace the user's collection" here,
      // because we never hold the collection — only the leaves that
      // moved.
      const writes = [];

      for (const k of Object.keys(next.best))
        if (next.best[k] !== prev.best[k])
          writes.push([`${root}/best/${k}`, (cur) => Math.max(cur || 0, next.best[k])]);

      for (const k of Object.keys(next.cleared))
        if (!prev.cleared[k]) writes.push([`${root}/cleared/${k}`, () => true]);

      for (const k of Object.keys(next.gauntlet))
        if (!prev.gauntlet[k]) writes.push([`${root}/gauntlet/${k}`, () => true]);

      if (next.tiltTokens !== prev.tiltTokens) {
        const d = next.tiltTokens - prev.tiltTokens;
        // += d, NOT = next. The server value may have moved since we
        // read it (another tab, another device). A delta converges.
        // A set clobbers. THIS LINE IS THE HOLO-WIPE LESSON.
        writes.push([`${root}/tiltTokens`, (cur) => Math.max(0, (cur || 0) + d)]);
      }

      for (const k of Object.keys(next.stats))
        if (next.stats[k] !== prev.stats[k]) {
          const d = next.stats[k] - prev.stats[k];
          writes.push([`${root}/stats/${k}`, (cur) => Math.max(0, (cur || 0) + d)]);
        }

      if (next.loadout !== prev.loadout)
        writes.push([`${root}/loadout`, () => next.loadout]);   // 4-slot choice: replace is correct

      // Fire them all. A failure on one leaf must not roll back the
      // others — they're independent facts. We keep the optimistic
      // local state either way; the next read reconciles. A player
      // who beat a trial and then lost their wifi still beat the trial.
      await Promise.all(writes.map(([path, fn]) =>
        runTransaction(ref(db, path), fn).catch((e) =>
          console.warn(`[progression] write failed @ ${path}:`, e?.message)
        )
      ));

      return next;
    },
  };
};

// ══════════════════════════════════════════════════════════════
// THE FACADE — what App.js actually touches. One driver at a time.
// ══════════════════════════════════════════════════════════════
let _driver = LocalDriver;

/** Swap the driver. P1.2 is literally this call, once, at boot:
 *      setDriver(FirebaseDriver({ db, ref, get, runTransaction, uid }));
 *  ...AFTER rules.json is live. Not before. */
export const setDriver = (d) => {
  _driver = d && typeof d.read === "function" && typeof d.commit === "function"
    ? d : LocalDriver;
};
export const getDriver = () => _driver;

export const read   = ()             => _driver.read();
export const commit = (prev, delta)  => _driver.commit(prev, delta);

/** Apply several deltas as one commit. Composition, not batching —
 *  a finished trial is ONE fact (score + cleared + tokens), so it
 *  should be one write, not three racing ones. */
export const commitAll = (prev, deltas) =>
  _driver.commit(prev, (s) => deltas.reduce((acc, d) => d(acc), s));

// ── Cross-app identity (P1.4 — the IE standard) ───────────────
// ?uid=UID → read it, USE it, then strip it from the URL so it
// doesn't linger in history. Same handoff Pier uses to route into
// Terror and Snake Eyes. Any IE title joining the ecosystem
// implements THIS, not a new scheme — that's what keeps the hub
// able to route uniformly.
export const readHandoffUid = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const uid = p.get("uid");
    if (!uid) return null;
    p.delete("uid");
    const q = p.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
    return uid;
  } catch { return null; }
};

export default {
  EMPTY, migrate, SCHEMA_V, TOKENS,
  dBest, dCleared, dGauntlet, dLoadout, dTokens, dStat,
  LocalDriver, FirebaseDriver,
  setDriver, getDriver, read, commit, commitAll,
  readHandoffUid,
};
